import React from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { formatNumber, formatPercent } from '../../i18n/format'
import { AppUseSelector } from '../../redux/hooks'
import { Mastery5 } from '../../redux/reducers/wordsReducer'

// Single source of truth for tier ordering + display. Order is "most learned
// → least learned" so the stacked bar reads left → right with "New" on the
// far right (the unlearned tail) and the legend mirrors that flow. Labels are translation keys.
const TIERS: { key: Mastery5; label: string; color: string }[] = [
  { key: 'mastered', label: 'words:tiers.mastered', color: '#006d4e' },
  { key: 'developing', label: 'words:tiers.developing', color: '#5aa07a' },
  { key: 'growing', label: 'words:tiers.growing', color: '#d97f3c' },
  { key: 'learning', label: 'words:tiers.learning', color: '#e8b04a' },
  { key: 'new', label: 'words:tiers.new', color: '#d8d8d4' },
]

// 12345 -> '12k', 1234 -> '1.2k', 999 -> '999'.
const formatCount = (n: number): string => {
  const thousands = (digits: number) =>
    i18n.t('words:progress.thousands', {
      value: formatNumber(n / 1000, { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    })
  if (n >= 10000) return thousands(0)
  if (n >= 1000) return thousands(1)
  return formatNumber(n, { useGrouping: false })
}

const WordsProgress: React.FC = () => {
  const { t } = useTranslation('words')
  const { stats, statsTotal, statsLoading, filters } = AppUseSelector(({ words }) => words)
  const total = statsTotal || 0

  const segments = TIERS.map((t) => ({ ...t, count: stats[t.key] || 0 }))
  // Compute segment widths from counts; learned% = everything past New.
  const newCount = stats.new || 0
  const learnedCount = Math.max(0, total - newCount)
  const learnedPct = total ? Math.round((learnedCount / total) * 100) : 0

  const hasAnyData = total > 0
  const hasFilters =
    !!filters.search ||
    !!filters.levels.hsk.length ||
    !!filters.levels.newHsk.length ||
    !!filters.levels.tofcl.length

  return (
    <div className='words-progress'>
      <div className='words-progress-head'>
        <div className='words-progress-title'>
          <span className='words-progress-pct'>{formatPercent(learnedPct / 100)}</span>
          <span className='words-progress-sub'>
            {hasFilters
              ? t('progress.summaryFiltered', { learned: formatCount(learnedCount), total: formatCount(total) })
              : t('progress.summary', { learned: formatCount(learnedCount), total: formatCount(total) })}
          </span>
        </div>
      </div>

      <div className='words-progress-bar' role='img' aria-label={t('progress.barLabel', { percent: formatPercent(learnedPct / 100) })}>
        {hasAnyData ? (
          segments.map((s) =>
            s.count > 0 ? (
              <div
                key={s.key}
                className='words-progress-seg'
                style={{ width: `${(s.count / total) * 100}%`, background: s.color }}
                title={t('progress.segmentTitle', { tier: t(s.label), value: formatNumber(s.count) })}
              />
            ) : null,
          )
        ) : (
          <div className='words-progress-seg-empty' />
        )}
      </div>

      <ul className='words-progress-legend'>
        {segments.map((s) => (
          <li key={s.key} className={`words-progress-legend-item${statsLoading ? ' loading' : ''}`}>
            <span className='words-progress-swatch' style={{ background: s.color }} aria-hidden='true' />
            <span className='words-progress-label'>{t(s.label)}</span>
            <span className='words-progress-count'>{formatNumber(s.count)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default WordsProgress
